import { useState, useEffect, useCallback, memo } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Zap, Activity } from 'lucide-react';

// 2026-09-29 — every row carries the same lightning bolt; the entity-type
// colour is the only per-row signal. The old per-event map (Trophy for
// award/election wins, TrendingUp for product launches) read as a glitch
// next to 40 bolts ("why does Musk get an arrow?") on both web and mobile.
const EVENT_ICON = Zap;

const TYPE_COLORS = {
  athlete: 'text-green-500', ceo: 'text-blue-500', actor: 'text-orange-500',
  musician: 'text-yellow-500', country_leader: 'text-purple-500',
  influencer: 'text-pink-500',
};

export default memo(function LiveScoringFeed({ onEntityClick }) {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItems, setNewItems] = useState([]);
  // v5.8.9 — fetch the canonical active-event-types set once on mount
  // so we can silently filter out legacy event types from old scoring
  // rows (`viral_moment`, `charity_event`, `scandal`, `controversy`,
  // etc. — removed in v5.7.12 for defamation safety but still in the
  // DB). null sentinel = "couldn't fetch the canonical list, show
  // everything" (graceful degradation).
  const [activeEventTypes, setActiveEventTypes] = useState(null);

  const loadFeed = useCallback(async () => {
    try {
      const [feedRes, activeRes] = await Promise.all([
        api.get('/scoring/live', { params: { limit: 30 } }),
        api.get('/scoring/active-event-types').catch(() => ({ data: { active_event_types: null } })),
      ]);
      setFeed(feedRes.data.feed || []);
      setActiveEventTypes(activeRes.data.active_event_types
        ? new Set(activeRes.data.active_event_types)
        : null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // Poll for updates every 15 seconds
  useEffect(() => {
    let highlightTimer = null;
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/scoring/live', { params: { limit: 5 } });
        const latest = res.data.feed || [];
        const existingIds = new Set(feed.map(f => f.id));
        const newOnes = latest.filter(f => !existingIds.has(f.id));
        if (newOnes.length > 0) {
          setNewItems(newOnes);
          setFeed(prev => [...newOnes, ...prev].slice(0, 50));
          // v7 (Gemini audit 2026-06, P2): track + clear the highlight timer
          // so overlapping polls / unmount can't leave stale highlights or
          // leak a pending timeout.
          if (highlightTimer) clearTimeout(highlightTimer);
          highlightTimer = setTimeout(() => setNewItems([]), 3000);
        }
      } catch (e) { /* ignore */ }
    }, 15000);
    return () => { clearInterval(interval); if (highlightTimer) clearTimeout(highlightTimer); };
  }, [feed]);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
      </div>
    );
  }

  return (
    <div data-testid="live-scoring-feed">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4 text-[hsl(var(--accent))]" />
        <h3 className="text-sm font-semibold">Live Scoring</h3>
        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
      </div>

      <ScrollArea className="h-[300px]">
        <div className="space-y-1.5">
          {(() => {
            // v5.8.9 — filter out deprecated event types (see v5.7.12
            // defamation-safety removals). `activeEventTypes === null`
            // means we couldn't fetch the canonical list; fall back to
            // showing everything rather than nothing.
            const visibleFeed = activeEventTypes
              ? feed.filter(item => activeEventTypes.has(item.event_type))
              : feed;
            if (visibleFeed.length === 0) {
              return <p className="text-sm text-muted-foreground text-center py-8">No scoring events yet</p>;
            }
            return visibleFeed.map((item) => {
              const Icon = EVENT_ICON;
              const isNew = newItems.some(n => n.id === item.id);
              const canClick = !!(onEntityClick && item.entity_id);
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl border border-border/40 bg-card ${
                    isNew ? 'ring-1 ring-[hsl(var(--accent))] bg-[hsl(var(--accent)/0.05)]' : ''
                  } ${canClick ? 'cursor-pointer hover:bg-secondary/30' : ''}`}
                  onClick={canClick ? () => onEntityClick(item.entity_id) : undefined}
                  role={canClick ? 'button' : undefined}
                  tabIndex={canClick ? 0 : undefined}
                  onKeyDown={canClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntityClick(item.entity_id); } } : undefined}
                  data-testid={canClick ? `live-scoring-row-${item.entity_id}` : undefined}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${TYPE_COLORS[item.entity_type] || 'text-muted-foreground'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">
                      <span className="font-semibold">{item.entity_name}</span>
                      <span className="text-muted-foreground"> • {item.event_type?.replace(/_/g, ' ')}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-bold tabular-nums text-[hsl(var(--positive-text))]">
                      {formatFranc(item.points, { sign: true })}
                    </span>
                    {item.multiplier > 1 && (
                      <Badge variant="secondary" className="text-[8px] px-1">{item.multiplier}x</Badge>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </ScrollArea>
    </div>
  );
});
